/**
 * Zahlweg-Hinweis (Mimmo, 02.11.2026): „Ein Hinweis daneben, ob die Gäste per Rechnung nach der
 * Veranstaltung, am Veranstaltungstag oder davor per Stripe zahlen.“
 *
 * EINE Texte-Funktion für beide Orte (Kopf des Vorgangs, Karte der Buchungsliste). Rein anzeigend:
 * welcher Weg gilt, hat der Server aus den GELTENDEN Konditionen aufgelöst (`zahlweg` in
 * `GET /api/events/:id` und in den Tatsachen von `GET /api/inquiries`, `apps/api/src/lib/
 * zahlweg-auskunft.ts`) — hier wird nichts abgeleitet und nichts gerechnet, nur beschriftet.
 * Die Beträge stehen woanders; dies ist die Auskunft „WIE“, nie „WIEVIEL“.
 */
export interface ZahlwegDaten {
  pfad: "stripe" | "kostenuebernahme";
  anzahlung: "keine" | "stripe" | "vor_ort" | "rechnung";
  rest: "keiner" | "stripe_vorab" | "vor_ort" | "rechnung_vorab" | "rechnung_nach" | null;
  /** YYYY-MM-DD, nur bei Rest vorab. */
  restFaelligAm: string | null;
  eingefroren?: boolean;
}

export interface ZahlwegText {
  label: string;
  icon: string;
  /** Vollständiger Satz für den Tooltip. */
  title: string;
}

/** „2026-11-04“ → „04.11.“ (ohne Jahr, wie die übrigen Datumskürzel in den Pillen). */
function kurzDatum(iso: string | null | undefined): string | null {
  const m = iso?.match(/^\d{4}-(\d{2})-(\d{2})/);
  return m ? `${m[2]}.${m[1]}.` : null;
}

const REST_ICON: Record<string, string> = {
  stripe_vorab: "credit_card",
  rechnung_vorab: "receipt_long",
  rechnung_nach: "receipt_long",
  vor_ort: "storefront",
};

function restText(z: ZahlwegDaten): string | null {
  switch (z.rest) {
    case "stripe_vorab": {
      const bis = kurzDatum(z.restFaelligAm);
      return bis ? `per Stripe vorab (bis ${bis})` : "per Stripe vorab";
    }
    case "rechnung_vorab":
      return "Rechnung vorab";
    case "rechnung_nach":
      return "Rechnung nach der Veranstaltung";
    case "vor_ort":
      return "vor Ort am Veranstaltungstag";
    default:
      return null;
  }
}

const ANZAHLUNG_TEXT: Record<string, string> = {
  stripe: "Anzahlung per Stripe",
  vor_ort: "Anzahlung vor Ort",
  rechnung: "Anzahlung per Rechnung vorab",
};

/**
 * @param zahlweg  die Auskunft des Servers; ohne sie wird nichts behauptet (`null`)
 * @param stand    netto Bezahltes und Angebotssumme in Cent — beides vom Server
 *
 *   Kostenübernahme                       → „Kostenübernahme“
 *   alles bezahlt                         → „vollständig bezahlt“
 *   Anzahlung eingegangen, Rest offen     → „Rest: <Weg>“
 *   noch nichts eingegangen               → „Zahlung: <Anzahlung bzw. Weg>“
 *   Weg nicht hinterlegt / keine Summe    → nichts
 */
export function zahlwegText(
  zahlweg: ZahlwegDaten | null | undefined,
  stand: { bezahltCents?: number | null; gesamtCents?: number | null; ohneVollstaendig?: boolean },
): ZahlwegText | null {
  if (!zahlweg) return null;
  if (zahlweg.pfad === "kostenuebernahme") {
    return { label: "Kostenübernahme", icon: "handshake", title: "Der Betrieb rechnet über die Kostenübernahme ab, keine einzelne Zahlung des Gastes." };
  }
  const gesamt = stand.gesamtCents ?? 0;
  const bezahlt = stand.bezahltCents ?? 0;
  if (gesamt <= 0) return null;
  if (bezahlt >= gesamt) {
    // Codex-Befund PR #1884: die Buchungskarte kennt die maßgebliche Deckung (Abrechnung, externe Zahlungen) nicht –
    // dort wird „vollständig bezahlt“ nie behauptet, lieber nichts als eine widersprüchliche Auskunft.
    if (stand.ohneVollstaendig) return null;
    return { label: "vollständig bezahlt", icon: "check_circle", title: "Der Betrag ist vollständig eingegangen." };
  }

  const rest = restText(zahlweg);
  const icon = REST_ICON[zahlweg.rest ?? ""] ?? "payments";
  if (bezahlt > 0) {
    if (!rest) return null;
    return { label: `Rest: ${rest}`, icon, title: `Anzahlung ist eingegangen. Der Rest wird so beglichen: ${rest}.` };
  }
  const anzahlung = ANZAHLUNG_TEXT[zahlweg.anzahlung];
  if (anzahlung) {
    return {
      label: `Zahlung: ${anzahlung}`,
      icon: zahlweg.anzahlung === "stripe" ? "credit_card" : zahlweg.anzahlung === "vor_ort" ? "storefront" : "receipt_long",
      title: rest ? `${anzahlung}. Rest: ${rest}.` : `${anzahlung}.`,
    };
  }
  if (!rest) return null;
  return { label: `Zahlung: ${rest}`, icon, title: `Keine Anzahlung vorgesehen. Bezahlt wird so: ${rest}.` };
}
